import logger from "../utils/logger";
import posthog from "posthog-js";
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  getConsent,
  saveConsent,
  hasConsentChoice,
  checkIsExistingUser,
  saveConsentToDatabase,
  getCurrentVisitorId,
  syncConsentOnLogin,
} from '../utils/consentManager';
import {
  loadScriptsBasedOnConsent,
  setGTMDefaultConsent,
  disablePostHogTracking,
} from '../utils/trackingScripts';

// Context for cookie consent
const CookieConsentContext = createContext(null);

/**
 * Cookie Consent Provider
 * Wraps the app and provides consent state and actions
 */
export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showExpanded, setShowExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [bannerShownTime, setBannerShownTime] = useState(null);

  // Initialize consent on mount
  useEffect(() => {
    const initializeConsent = async () => {
      try {
        // Check for existing consent cookie
        const existingConsent = getConsent();
        
        if (existingConsent) {
          // User has already consented
          setConsent(existingConsent);
          loadScriptsBasedOnConsent(existingConsent);
          setShowBanner(false);
          setIsLoading(false);
          return;
        }
        
        // Check if user is logged in
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Check if existing user (has PageEvent records)
          const isExisting = await checkIsExistingUser(user.id);
          
          if (isExisting) {
            // Auto-consent for existing users
            const autoConsent = {
              analytics: true,
              marketing: true,
              method: 'existing_user'
            };
            
            const savedConsent = saveConsent(autoConsent);
            setConsent(savedConsent);
            loadScriptsBasedOnConsent(savedConsent);
            
            // Save to database (best effort)
            saveConsentToDatabase(autoConsent, getCurrentVisitorId());
            
            setShowBanner(false);
            setIsLoading(false);
            return;
          }
        }
        
        // New user - set default consent (denied) and show banner
        // PostHog stays in cookieless memory-only mode (no opt-out needed)
        // so basic anonymous analytics still work without cookies
        setGTMDefaultConsent();
        setShowBanner(true);
        setBannerShownTime(Date.now());
        
        // Track banner shown (in cookieless mode)
        posthog.capture('cookie_banner_shown', {
          page: window.location.pathname,
        });
        
        setIsLoading(false);
      } catch (err) {
        logger.error('Error initializing consent:', err);
        setIsLoading(false);
        setShowBanner(true);
      }
    };
    
    initializeConsent();
  }, []);

  // Listen for auth state changes to sync consent and identify user in PostHog
  useEffect(() => {
    // Guard: only sync once per user per session to prevent looping calls
    const syncedUsersRef = new Set();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const userId = session.user.id;
        if (!syncedUsersRef.has(userId)) {
          syncedUsersRef.add(userId);
          const visitorId = getCurrentVisitorId();
          syncConsentOnLogin(visitorId);
        }
        
        // Identify user in PostHog so events are linked to their profile
        posthog.identify(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Calculate time to decision for analytics
  const getTimeToDecision = useCallback(() => {
    if (bannerShownTime) {
      return Math.round((Date.now() - bannerShownTime) / 1000);
    }
    return null;
  }, [bannerShownTime]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent = {
      analytics: true,
      marketing: true,
      method: 'accept_all'
    };
    
    const savedConsent = saveConsent(newConsent);
    setConsent(savedConsent);
    loadScriptsBasedOnConsent(savedConsent);
    
    // Save to database (best effort)
    saveConsentToDatabase(newConsent, getCurrentVisitorId());
    
    // Track event
    if (window.posthog) {
      window.posthog.capture('cookie_banner_accept_all', {
        page: window.location.pathname,
        time_to_decision: getTimeToDecision(),
      });
    }
    
    setShowBanner(false);
    setShowExpanded(false);
  }, [getTimeToDecision]);

  // Reject all optional cookies
  const rejectAll = useCallback(() => {
    const newConsent = {
      analytics: false,
      marketing: false,
      method: 'reject_all'
    };
    
    const savedConsent = saveConsent(newConsent);
    setConsent(savedConsent);
    
    // Save to database (best effort)
    saveConsentToDatabase(newConsent, getCurrentVisitorId());
    
    // Track event BEFORE disabling (otherwise the capture is silently dropped)
    if (window.posthog) {
      window.posthog.capture('cookie_banner_reject_all', {
        page: window.location.pathname,
        time_to_decision: getTimeToDecision(),
      });
    }
    
    // Don't load tracking scripts, keep PostHog disabled
    disablePostHogTracking();
    
    setShowBanner(false);
    setShowExpanded(false);
  }, [getTimeToDecision]);

  // Save custom preferences
  const saveCustomConsent = useCallback((analytics, marketing) => {
    const newConsent = {
      analytics,
      marketing,
      method: 'custom'
    };
    
    const savedConsent = saveConsent(newConsent);
    setConsent(savedConsent);
    loadScriptsBasedOnConsent(savedConsent);
    
    // Save to database (best effort)
    saveConsentToDatabase(newConsent, getCurrentVisitorId());
    
    // Track event
    if (window.posthog) {
      window.posthog.capture('cookie_banner_custom_saved', {
        page: window.location.pathname,
        analytics,
        marketing,
        time_to_decision: getTimeToDecision(),
      });
    }
    
    setShowBanner(false);
    setShowExpanded(false);
  }, [getTimeToDecision]);

  // Open settings (from footer or privacy page)
  const openSettings = useCallback((source = 'footer') => {
    setShowBanner(true);
    setShowExpanded(true); // Show expanded view with current toggles
    setBannerShownTime(Date.now());
    
    // Track event
    if (window.posthog) {
      window.posthog.capture('cookie_settings_opened', {
        source,
      });
    }
  }, []);

  // Toggle manage preferences
  const toggleExpanded = useCallback(() => {
    const newExpanded = !showExpanded;
    setShowExpanded(newExpanded);
    
    if (newExpanded && window.posthog) {
      window.posthog.capture('cookie_banner_manage_opened', {
        page: window.location.pathname,
      });
    }
  }, [showExpanded]);

  // Close banner (only for settings re-access, not initial)
  const closeBanner = useCallback(() => {
    if (hasConsentChoice()) {
      setShowBanner(false);
      setShowExpanded(false);
    }
  }, []);

  const value = {
    consent,
    showBanner,
    showExpanded,
    isLoading,
    acceptAll,
    rejectAll,
    saveCustomConsent,
    openSettings,
    toggleExpanded,
    closeBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

/**
 * Hook to use cookie consent
 */
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  
  return context;
};

export default useCookieConsent;
